import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { services } from "@/data/servicesData";
import useSeo from "@/lib/useSeo";
import { breadcrumbSchema } from "@/lib/schema";

export default function Services() {
  useSeo({
    title: "Dental RCM Services | Billing, Verification, AR & More | ClariDent RCM",
    description: "Complete dental RCM services: insurance verification, dental billing, AR follow-up, payment posting, patient collections, credentialing, EFT/ERA enrollment, denial management, and reporting.",
    path: "/services",
    schemas: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])],
  });

  return (
    <main data-testid="services-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">What We Do</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900" data-testid="services-page-heading">
            Complete Dental RCM Services
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            From verification to collections, we manage the revenue cycle so your practice can focus on patients.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white p-7 group hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`service-card-${i}`}
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-600 transition-colors duration-300">
                  <s.icon size={22} className="text-teal-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h2 className="font-display text-xl font-semibold text-navy-900 leading-snug">{s.title}</h2>
              </div>
              <ul className="space-y-2">
                {s.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 size={15} className="text-teal-600 shrink-0 mt-0.5" /> {item}
                  </li>
                ))}
              </ul>
              <Link to={`/services/${s.slug}`} data-testid={`service-learn-more-${s.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-teal-600 hover:gap-3 transition-all duration-200">
                Learn more <ArrowRight size={15} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-navy-950 px-8 py-14 sm:px-16 text-center relative overflow-hidden" data-testid="services-page-cta">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Not sure which service your practice needs most?</h2>
            <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-xl mx-auto">
              The free RCM audit answers that. We review your claims, denials, and AR — then tell you exactly where to start.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/book" data-testid="services-page-audit-btn" className="rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-xl">
                Get Your Free RCM Audit
              </Link>
              <Link to="/contact" data-testid="services-page-specialist-btn" className="rounded-full border-2 border-white/80 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-white/10">
                Talk to an RCM Specialist
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
